"use client";

import type React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Switch,
  Text,
  VStack,
  useToast,
  HStack,
  Radio,
  RadioGroup,
  Textarea,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Divider,
} from "@chakra-ui/react";
import {
  agentService,
  type VoicemailOption,
} from "../../../voice-services/agentService";

interface VoicemailSettingsProps {
  agentId: string;
  initialSettings?: {
    enable_voicemail_detection?: boolean;
    voicemail_message?: string;
    voicemail_detection_timeout_ms?: number;
    voicemail_option?: VoicemailOption | null;
  };
}

export const VoicemailSettings: React.FC<VoicemailSettingsProps> = ({
  agentId,
  initialSettings,
}) => {
  const toast = useToast();

  const getInitialAction = useCallback((): "hangup" | "message" => {
    // New format first
    const opt = initialSettings?.voicemail_option;
    if (opt?.action?.type) {
      return opt.action.type === "hangup" ? "hangup" : "message";
    }
    // Old format fallback
    return initialSettings?.voicemail_message ? "message" : "hangup";
  }, [initialSettings]);

  const getInitialMessage = useCallback((): string => {
    // New format first
    const opt = initialSettings?.voicemail_option;
    if (opt?.action?.type === "static_text") {
      return (opt.action as any).text || "";
    }
    // Old format fallback
    return initialSettings?.voicemail_message || "";
  }, [initialSettings]);

  const [settings, setSettings] = useState(initialSettings || {});
  const [voicemailAction, setVoicemailAction] = useState<"hangup" | "message">(
    getInitialAction(),
  );
  const [voicemailMessage, setVoicemailMessage] =
    useState<string>(getInitialMessage());
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    setSettings(initialSettings || {});
    setVoicemailAction(getInitialAction());
    setVoicemailMessage(getInitialMessage());
  }, [initialSettings, getInitialAction, getInitialMessage]);

  // Enabled if either the new-style option exists OR legacy boolean is true
  const isEnabled = useMemo(() => {
    return (
      (settings.voicemail_option !== undefined &&
        settings.voicemail_option !== null) ||
      !!settings.enable_voicemail_detection
    );
  }, [settings.voicemail_option, settings.enable_voicemail_detection]);

  const updateSettings = useCallback(
    async (newSettings: Partial<typeof settings>) => {
      setIsUpdating(true);
      try {
        console.log("[v0] VoicemailSettings - Updating with:", newSettings);
        await agentService.updateAgentSettings(agentId, newSettings);
        setSettings((prev) => ({ ...prev, ...newSettings }));
        toast({
          title: "Voicemail settings updated",
          status: "success",
          duration: 1800,
          isClosable: true,
        });
      } catch (error: any) {
        console.error("[v0] Error updating voicemail settings:", error);
        toast({
          title: "Error updating voicemail settings",
          description:
            error?.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, toast],
  );

  // ✅ Fix: define this handler (your compile error is because it was missing)
  const handleVoicemailMessageChange = (value: string) => {
    setVoicemailMessage(value);
  };

  const handleVoicemailDetectionChange = async (checked: boolean) => {
    if (checked) {
      const voicemailOption: VoicemailOption =
        voicemailAction === "hangup"
          ? { action: { type: "hangup" } }
          : { action: { type: "static_text", text: voicemailMessage || "" } };

      // instant UI feedback
      setSettings((prev) => ({
        ...prev,
        enable_voicemail_detection: true,
        voicemail_option: voicemailOption,
      }));

      await updateSettings({
        enable_voicemail_detection: true,
        voicemail_option: voicemailOption,
      });
    } else {
      // instant UI feedback
      setSettings((prev) => ({
        ...prev,
        enable_voicemail_detection: false,
        voicemail_option: null,
      }));

      await updateSettings({
        enable_voicemail_detection: false,
        voicemail_option: null,
      });
    }
  };

  const handleVoicemailActionChange = async (value: "hangup" | "message") => {
    setVoicemailAction(value);

    const voicemailOption: VoicemailOption =
      value === "hangup"
        ? { action: { type: "hangup" } }
        : { action: { type: "static_text", text: voicemailMessage || "" } };

    // Only push to backend if voicemail is enabled; otherwise just update local selection
    if (!isEnabled) return;

    await updateSettings({
      enable_voicemail_detection: true,
      voicemail_option: voicemailOption,
    });
  };

  const handleVoicemailMessageBlur = async () => {
    if (isEnabled && voicemailAction === "message") {
      const voicemailOption: VoicemailOption = {
        action: { type: "static_text", text: voicemailMessage },
      };
      await updateSettings({
        enable_voicemail_detection: true,
        voicemail_option: voicemailOption,
      });
    }
  };

  const handleTimeoutChange = async (value: number) => {
    await updateSettings({ voicemail_detection_timeout_ms: value });
  };

  const formatDetectionTimeout = (ms?: number) => {
    const val = ms ?? 30000;
    if (val >= 60000) return `${(val / 60000).toFixed(1)} min`;
    return `${Math.round(val / 1000)} s`;
  };

  return (
    <Box
      bg="white"
      border="1px"
      borderColor="gray.200"
      borderRadius="2xl"
      p={{ base: 4, md: 6 }}
      shadow="sm"
    >
      <FormControl>
        <HStack justify="space-between" align="flex-start">
          <Box>
            <FormLabel
              fontSize="sm"
              fontWeight="semibold"
              color="secondary.800"
              mb={1}
            >
              Voicemail Detection
            </FormLabel>
            <Text fontSize="xs" color="gray.600">
              Hang up or leave a voicemail if a voicemail is detected.
            </Text>
          </Box>

          <Switch
            isChecked={isEnabled}
            onChange={(e) => handleVoicemailDetectionChange(e.target.checked)}
            isDisabled={isUpdating}
            colorScheme="primary"
            size="md"
          />
        </HStack>

        {isEnabled && (
          <VStack spacing={5} mt={5} align="stretch">
            <Divider borderColor="gray.200" />

            <Box>
              <FormLabel
                fontSize="sm"
                fontWeight="semibold"
                color="secondary.800"
                mb={2}
              >
                Action on Voicemail
              </FormLabel>
              <RadioGroup
                value={voicemailAction}
                onChange={(v) => handleVoicemailActionChange(v as any)}
              >
                <VStack align="start" spacing={3}>
                  <Radio value="hangup" colorScheme="primary">
                    <Text fontSize="sm" color="gray.800">
                      Hang up if reaching voicemail
                    </Text>
                  </Radio>
                  <Radio value="message" colorScheme="primary">
                    <Text fontSize="sm" color="gray.800">
                      Leave a message if reaching voicemail
                    </Text>
                  </Radio>
                </VStack>
              </RadioGroup>
            </Box>

            {voicemailAction === "message" && (
              <Box>
                <FormLabel
                  fontSize="sm"
                  fontWeight="semibold"
                  color="secondary.800"
                  mb={2}
                >
                  Voicemail Message
                </FormLabel>
                <Textarea
                  value={voicemailMessage}
                  onChange={(e) => handleVoicemailMessageChange(e.target.value)}
                  onBlur={handleVoicemailMessageBlur}
                  placeholder="Message content — use {{variable}} placeholders"
                  size="sm"
                  resize="vertical"
                  minH="90px"
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "primary.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-primary-500)",
                  }}
                />
              </Box>
            )}

            <Box>
              <FormLabel
                fontSize="sm"
                fontWeight="semibold"
                color="secondary.800"
                mb={1}
              >
                Voicemail Detection Duration
              </FormLabel>
              <Text fontSize="xs" color="gray.600" mb={3}>
                Duration for which voicemail detection will be active during the
                call.
              </Text>

              <HStack spacing={4} align="center">
                <Slider
                  aria-label="Voicemail detection timeout"
                  min={5000}
                  max={180000}
                  step={1000}
                  value={settings.voicemail_detection_timeout_ms ?? 30000}
                  onChange={(value) =>
                    setSettings((prev) => ({
                      ...prev,
                      voicemail_detection_timeout_ms: value,
                    }))
                  }
                  onChangeEnd={handleTimeoutChange}
                  isDisabled={isUpdating}
                  colorScheme="primary"
                  flex={1}
                >
                  <SliderTrack bg="gray.200">
                    <SliderFilledTrack />
                  </SliderTrack>
                  <SliderThumb />
                </Slider>

                <Text
                  fontSize="sm"
                  color="gray.800"
                  minW="56px"
                  textAlign="right"
                >
                  {formatDetectionTimeout(
                    settings.voicemail_detection_timeout_ms,
                  )}
                </Text>
              </HStack>
            </Box>
          </VStack>
        )}
      </FormControl>
    </Box>
  );
};

export default VoicemailSettings;
